import axios from 'axios'
import { clearAdminToken, getAdminToken } from '../utils/adminAuth'
import { ADMIN_LOGIN_PATH, isAdminPath } from '../constants/adminRoutes'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && isAdminPath(window.location.pathname)) {
      clearAdminToken()
      if (window.location.pathname !== ADMIN_LOGIN_PATH) {
        window.location.href = ADMIN_LOGIN_PATH
      }
    }
    return Promise.reject(error)
  },
)

export const authApi = {
  login: (credentials) => api.post('/admin/login', credentials),
  logout: () => api.post('/admin/logout'),
  me: () => api.get('/admin/me'),
}

export const profileApi = {
  get: () => api.get('/admin/profile'),
  updatePassword: (data) => api.put('/admin/profile/password', data),
}

export const notificationsApi = {
  getAll: () => api.get('/admin/notifications'),
  markRead: (data) => api.post('/admin/notifications/read', data),
}

export const menuApi = {
  getAll: () => api.get('/menus'),
}

export const settingsApi = {
  get: () => api.get('/settings'),
  getAllAdmin: () => api.get('/admin/settings'),
  create: (data) => api.post('/admin/settings', data),
  update: (id, data) => api.put(`/admin/settings/${id}`, data),
  delete: (id) => api.delete(`/admin/settings/${id}`),
}

export const pagesApi = {
  getAll: (params) => api.get('/pages', { params }),
  getAllAdmin: (params) => api.get('/admin/pages', { params }),
  getBySlug: (slug) => api.get(`/pages/${slug}`),
  create: (data) =>
    data instanceof FormData
      ? api.post('/admin/pages', data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.post('/admin/pages', data),
  update: (id, data) =>
    data instanceof FormData
      ? api.post(`/admin/pages/${id}?_method=PUT`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.put(`/admin/pages/${id}`, data),
  delete: (id) => api.delete(`/admin/pages/${id}`),
}

export const menusApi = {
  getAll: () => api.get('/menus'),
  getAllAdmin: (params) => api.get('/admin/menus', { params }),
  create: (data) => api.post('/admin/menus', data),
  update: (id, data) => api.put(`/admin/menus/${id}`, data),
  delete: (id) => api.delete(`/admin/menus/${id}`),
}

export const newsApi = {
  getAll: (params) => api.get('/news', { params }),
  getAllAdmin: (params) => api.get('/admin/news', { params }),
  getBySlug: (slug) => api.get(`/news/${slug}`),
  create: (data) =>
    data instanceof FormData
      ? api.post('/admin/news', data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.post('/admin/news', data),
  update: (id, data) =>
    data instanceof FormData
      ? api.post(`/admin/news/${id}?_method=PUT`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.put(`/admin/news/${id}`, data),
  delete: (id) => api.delete(`/admin/news/${id}`),
}

export const eventsApi = {
  getAll: (params) => api.get('/events', { params }),
  getAllAdmin: (params) => api.get('/admin/events', { params }),
  getBySlug: (slug) => api.get(`/events/${slug}`),
  create: (data) =>
    data instanceof FormData
      ? api.post('/admin/events', data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.post('/admin/events', data),
  update: (id, data) =>
    data instanceof FormData
      ? api.post(`/admin/events/${id}?_method=PUT`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.put(`/admin/events/${id}`, data),
  delete: (id) => api.delete(`/admin/events/${id}`),
}

export const slidersApi = {
  getAll: () => api.get('/sliders'),
  getAllAdmin: () => api.get('/admin/sliders'),
  create: (data) => api.post('/admin/sliders', data),
  update: (id, data) =>
    data instanceof FormData
      ? api.post(`/admin/sliders/${id}?_method=PUT`, data)
      : api.put(`/admin/sliders/${id}`, data),
  delete: (id) => api.delete(`/admin/sliders/${id}`),
  syncHomepage: () => api.post('/admin/sliders/sync-homepage'),
}

export const storyCardsApi = {
  getAll: (params) => api.get('/story-cards', { params }),
  getAllAdmin: (params) => api.get('/admin/story-cards', { params }),
  create: (data) =>
    data instanceof FormData
      ? api.post('/admin/story-cards', data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.post('/admin/story-cards', data),
  update: (id, data) =>
    data instanceof FormData
      ? api.post(`/admin/story-cards/${id}?_method=PUT`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.put(`/admin/story-cards/${id}`, data),
  delete: (id) => api.delete(`/admin/story-cards/${id}`),
}

export const galleryApi = {
  getAll: (params) => api.get('/galleries', { params }),
  getAllAdmin: (params) => api.get('/admin/galleries', { params }),
  create: (data) =>
    data instanceof FormData
      ? api.post('/admin/galleries', data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.post('/admin/galleries', data),
  update: (id, data) =>
    data instanceof FormData
      ? api.post(`/admin/galleries/${id}?_method=PUT`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.put(`/admin/galleries/${id}`, data),
  delete: (id) => api.delete(`/admin/galleries/${id}`),
}

export const applicationsApi = {
  submit: (data) => api.post('/applications', data),
  getAllAdmin: (params) => api.get('/admin/applications', { params }),
  get: (id) => api.get(`/admin/applications/${id}`),
  updateStatus: (id, data) => api.patch(`/admin/applications/${id}`, data),
  delete: (id) => api.delete(`/admin/applications/${id}`),
}

export const counselingApi = {
  submit: (data) => api.post('/counseling-appointments', data),
}

export const counselingAdminApi = {
  getAllAdmin: (params) => api.get('/admin/counseling-appointments', { params }),
  get: (id) => api.get(`/admin/counseling-appointments/${id}`),
  updateStatus: (id, data) => api.patch(`/admin/counseling-appointments/${id}`, data),
}

export const homepageApi = {
  getAll: () => api.get('/homepage'),
  getAllAdmin: () => api.get('/admin/homepage-sections'),
  get: (id) => api.get(`/admin/homepage-sections/${id}`),
  create: (data) =>
    data instanceof FormData
      ? api.post('/admin/homepage-sections', data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.post('/admin/homepage-sections', data),
  update: (id, data) =>
    data instanceof FormData
      ? api.post(`/admin/homepage-sections/${id}?_method=PUT`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
      : api.put(`/admin/homepage-sections/${id}`, data),
  delete: (id) => api.delete(`/admin/homepage-sections/${id}`),
  reorder: (order) => api.post('/admin/homepage-sections/reorder', { order }),
}

export const dashboardApi = {
  get: () => api.get('/admin/dashboard'),
}

export const mediaApi = {
  upload: (formData) =>
    api.post('/admin/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
}

export const usersApi = {
  getAll: () => api.get('/admin/users'),
  get: (id) => api.get(`/admin/users/${id}`),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  delete: (id) => api.delete(`/admin/users/${id}`),
  getModules: () => api.get('/admin/permissions/modules'),
}

export const activityLogsApi = {
  getAll: (params) => api.get('/admin/activity-logs', { params }),
}

export const formSubmissionsApi = {
  getAll: (params) => api.get('/admin/form-submissions', { params }),
  getContact: (id) => api.get(`/admin/contact-submissions/${id}`),
  updateContact: (id, data) => api.patch(`/admin/contact-submissions/${id}`, data),
}

export const contactApi = {
  submit: (data) => api.post('/contact', data),
}

export const jobsApi = {
  getDepartments: () => api.get('/careers/departments'),
  apply: (formData) =>
    api.post('/careers/apply', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
}

export const careersApi = jobsApi

export const recruitmentAdminApi = {
  applications: {
    getStats: () => api.get('/admin/job-applications/stats'),
    getAll: (params) => api.get('/admin/job-applications', { params }),
    get: (id) => api.get(`/admin/job-applications/${id}`),
    updateStatus: (id, data) => api.put(`/admin/job-applications/${id}/status`, data),
    addNote: (id, data) => api.post(`/admin/job-applications/${id}/notes`, data),
    sendEmail: (id, data) => api.post(`/admin/job-applications/${id}/email`, data),
    exportExcel: (params) => api.get('/admin/job-applications/export', { params, responseType: 'blob' }),
    exportPdf: (id) => api.get(`/admin/job-applications/${id}/export-pdf`, { responseType: 'blob' }),
    downloadAttachment: (applicationId, attachmentId) =>
      api.get(`/admin/job-applications/${applicationId}/attachments/${attachmentId}/download`, {
        responseType: 'blob',
      }),
    viewAttachment: (applicationId, attachmentId) =>
      api.get(`/admin/job-applications/${applicationId}/attachments/${attachmentId}/view`, {
        responseType: 'blob',
      }),
    delete: (id) => api.delete(`/admin/job-applications/${id}`),
  },
  departments: {
    getAll: () => api.get('/admin/job-departments'),
    create: (data) => api.post('/admin/job-departments', data),
    update: (id, data) => api.put(`/admin/job-departments/${id}`, data),
    delete: (id) => api.delete(`/admin/job-departments/${id}`),
    specializations: {
      getAll: (departmentId) => api.get(`/admin/job-departments/${departmentId}/specializations`),
      create: (departmentId, data) => api.post(`/admin/job-departments/${departmentId}/specializations`, data),
      update: (departmentId, id, data) => api.put(`/admin/job-departments/${departmentId}/specializations/${id}`, data),
      delete: (departmentId, id) => api.delete(`/admin/job-departments/${departmentId}/specializations/${id}`),
    },
  },
  statuses: {
    getAll: () => api.get('/admin/job-application-statuses'),
    create: (data) => api.post('/admin/job-application-statuses', data),
    update: (id, data) => api.put(`/admin/job-application-statuses/${id}`, data),
    delete: (id) => api.delete(`/admin/job-application-statuses/${id}`),
  },
  settings: {
    get: () => api.get('/admin/recruitment-settings'),
    update: (data) => api.put('/admin/recruitment-settings', data),
  },
}

export const chatbotApi = {
  config: () => api.get('/chatbot/config'),
  message: (data) => api.post('/chatbot/message', data),
}

export const chatbotAdminApi = {
  getKnowledge: () => api.get('/admin/chatbot/knowledge'),
  createKnowledge: (data) => api.post('/admin/chatbot/knowledge', data),
  updateKnowledge: (id, data) => api.put(`/admin/chatbot/knowledge/${id}`, data),
  deleteKnowledge: (id) => api.delete(`/admin/chatbot/knowledge/${id}`),
  analytics: () => api.get('/admin/chatbot/analytics'),
}

export const weeklyScheduleApi = {
  get: (slug = 'WeeklySchedule412') => api.get(`/weekly-schedules/${slug}`),
}

export const weeklyScheduleAdminApi = {
  get: (slug = 'WeeklySchedule412') => api.get(`/admin/weekly-schedules/${slug}`),
  update: (slug, data) => api.put(`/admin/weekly-schedules/${slug}`, data),
}

export default api

